import * as helios from "@hyperionbt/helios"
//wraps helios.Program
export default class Program {
    #program

    constructor(parameters = {}) {
        // load all the dependencies
        const allDeps = []

        const depSrcs = new Map()

        function addDepSrcs(dep) {
            depSrcs.set(dep.name, dep.src)

            for (let d of dep.dependencies) {
                addDepSrcs(d)
            }
        }

        for (let d of allDeps) {
            addDepSrcs(d)
        }

        this.#program = helios.Program.new(`spending auction_english

const MIN_LOVELACE: Int = 3_000_000
const PROTOCOL_FEE_PERCENTAGE: Int = 4
const TREASURY_ADDRESS: Address = Address::new(
  Credential::new_pubkey(PubKeyHash::new(#)), Option[StakingCredential]::None
)

struct Datum {
  owner_address: Address
  deadline: Time
  reserve: Int
  increment: Int
  bidder_address: Address
}

enum Redeemer {
  Bid
  End
}

func main(datum: Datum, redeemer: Redeemer, ctx: ScriptContext) -> Bool {
  prev_output: TxOutput = ctx.get_current_input().output;

  auction_inputs: []TxInput = ctx.tx.inputs.filter((input: TxInput) -> {
    input.output.address == prev_output.address
  });
  assert(auction_inputs.length == 1, "Can only handle one Auction at once");

  auction_outputs: []TxOutput = ctx.tx.outputs.filter((output: TxOutput) -> {
    output.address == prev_output.address
  });

  redeemer.switch {
    Bid => {
      assert(datum.deadline > ctx.tx.time_range.end, "Deadline must be on");
      assert(auction_outputs.length == 1, "Can only produce a bid");

      next_output: TxOutput = auction_outputs.head;

      prev_lovelace: Int = prev_output.value.get_lovelace();
      bid_lovelace: Int = next_output.value.get_lovelace();
      assert(
        bid_lovelace >= datum.reserve + MIN_LOVELACE,
        "Must bid more than reserve plus min ADA"
      );

      bid_diff: Int = bid_lovelace - prev_lovelace;
      assert(
        bid_diff >= datum.increment,
        "Must bid more than the minimum increment"
      );

      assert(
        prev_output.value.get_assets() == next_output.value.get_assets(),
        "Must not change assets"
      );

      next_datum: Datum = next_output.datum.switch {
        i: Inline => Datum::from_data(i.data),
        else => error("Invalid auction output: missing inline datum")
      };
      assert(
        next_datum.owner_address == datum.owner_address &&
        next_datum.deadline == datum.deadline &&
        next_datum.reserve == datum.reserve &&
        next_datum.increment == datum.increment,
        "Cannot change anything in datum but the bidder address"
      );

      // Return previous bid
      if (prev_lovelace - MIN_LOVELACE > 0) {
        ctx.tx.outputs.any((output: TxOutput) -> {
          output.address == datum.bidder_address &&
          output.value.get_lovelace() == prev_lovelace - MIN_LOVELACE
        })
      } else {
        true
      }
    },
    End => {
      assert(datum.deadline < ctx.tx.time_range.start, "Deadline must have passed");
      assert(auction_outputs.length == 0, "Cannot produce a bid");

      total_lovelace: Int = prev_output.value.get_lovelace();
      owner_lovelace: Int = total_lovelace * (100 - PROTOCOL_FEE_PERCENTAGE) / 100;
      protocol_lovelace: Int = total_lovelace - owner_lovelace;

      // A fold/reduce may be more efficient but not worth it
      owner_gets_ada: Bool = ctx.tx.outputs.any((output: TxOutput) -> {
        output.address == datum.owner_address &&
        output.value.get_lovelace() >= owner_lovelace
      });

      protocol_gets_ada: Bool = ctx.tx.outputs.any((output: TxOutput) -> {
        output.address == TREASURY_ADDRESS &&
        output.value.get_lovelace() >= protocol_lovelace
      });

      bidder_gets_assets: Bool = ctx.tx.outputs.any((output: TxOutput) -> {
        output.address == datum.bidder_address &&
        output.value.get_assets() >= prev_output.value.get_assets()
      });

      owner_gets_ada &&
      protocol_gets_ada &&
      bidder_gets_assets
    }
  }
}
`, Array.from(depSrcs.values()))

        if (Object.keys(parameters).length > 0) {
            this.#program.parameters = parameters
        }
    }

    get name() {
        return this.#program.name
    }

    get paramTypes() {
        return this.#program.paramTypes
    }

    get parameters() {
        return this.#program.parameters
    }

    get types() {
        return this.#program.types
    }

    set parameters(params) {
        this.#program.parameters = params
    }

    compile(optimize = false) {
        return this.#program.compile(optimize)
    }

    evalParam(paramName) {
        return this.#program.evalParam(paramName)
    }
}
